import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { IconPrinter } from '../../components/layout/icons';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { useToast } from '../../lib/toast';
import { usePathologyReports, useUpdatePathologyReports } from './pathology.queries';
import { ageDisplay, formatPathologyDateTime } from './pathology.utils';
import type { InvestigationReportKind, PathologyReportBundle, PathologyReportLine, PathologyReportResult, ReportAttachment } from './types';

interface ReportDraft {
  lineId: string;
  chargeName: string;
  chargeLegacyId: number | null;
  reportKind: InvestigationReportKind | null;
  sample: string | null;
  vial: string | null;
  instrumentUsed: string | null;
  reportTemplate: string | null;
  reportTemplateResult: string | null;
  note: string | null;
  extraNote: string | null;
  reportResults: PathologyReportResult[];
}

const wbcDifferentialTests = new Set([
  'NEUTROPHILS',
  'LYMPHOCYTES',
  'MONOCYTES',
  'EOSINOPHILS',
  'BASOPHILS',
  'BLAST CELL',
  'PROMYELOCYTE',
  'MYELOCYTES',
  'METAMYELOCYTES',
  'BAND CELL',
  'ATYPICAL CELL',
  'ATYPICAL MONONUCLEAR CELL',
]);

const lineToDraft = (line: PathologyReportLine): ReportDraft => {
  const hasTemplate = Boolean(line.reportTemplate);
  return {
    lineId: line.lineId,
    chargeName: line.chargeName,
    chargeLegacyId: line.chargeLegacyId ?? null,
    reportKind: line.reportKind,
    sample: line.sample,
    vial: line.vial,
    instrumentUsed: line.instrumentUsed,
    reportTemplate: line.reportTemplate,
    reportTemplateResult: line.reportTemplateResult ?? line.reportTemplate,
    note: hasTemplate ? null : line.note ?? line.chargeNote,
    extraNote: hasTemplate ? null : line.extraNote,
    reportResults: hasTemplate ? [] : line.reportResults.map((result) => ({ ...result })),
  };
};

const readFileAsAttachment = (file: File): Promise<ReportAttachment> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string') resolve({ name: file.name, dataUrl: reader.result });
    else reject(new Error('The selected file could not be read.'));
  };
  reader.onerror = () => reject(new Error('The selected file could not be read.'));
  reader.readAsDataURL(file);
});

const updateDraft = (drafts: ReportDraft[], lineId: string, updater: (draft: ReportDraft) => ReportDraft) =>
  drafts.map((draft) => draft.lineId === lineId ? updater(draft) : draft);

const numberOrNull = (value: string | null) => {
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const allRangesEqual = (result: PathologyReportResult) =>
  result.maleLower === result.femaleLower
  && result.maleLower === result.childLower
  && result.maleUpper === result.femaleUpper
  && result.maleUpper === result.childUpper;

const rangeFor = (result: PathologyReportResult, bundle: PathologyReportBundle) => {
  if (allRangesEqual(result)) return { lower: result.maleLower, upper: result.maleUpper };
  if (bundle.patientSnapshot.ageYears > 12 && bundle.patientSnapshot.gender === 'Male') return { lower: result.maleLower, upper: result.maleUpper };
  if (bundle.patientSnapshot.ageYears > 12) return { lower: result.femaleLower, upper: result.femaleUpper };
  return { lower: result.childLower, upper: result.childUpper };
};

const resultStyle = (result: PathologyReportResult, bundle: PathologyReportBundle) => {
  const value = numberOrNull(result.resultValue);
  const range = rangeFor(result, bundle);
  const lower = numberOrNull(range.lower);
  const upper = numberOrNull(range.upper);
  if (value === null || lower === null || upper === null) return { color: 'black', fontSize: 15 };
  return { color: value >= lower && value <= upper ? 'black' : 'red', fontSize: 15 };
};

const referenceRangeText = (result: PathologyReportResult) => {
  if (allRangesEqual(result)) return [result.maleLower, result.maleUpper].filter(Boolean).join(' - ');
  return [
    result.maleLower && result.maleUpper ? `Male: ${result.maleLower} - ${result.maleUpper}` : '',
    result.femaleLower && result.femaleUpper ? `Female: ${result.femaleLower} - ${result.femaleUpper}` : '',
    result.childLower && result.childUpper ? `Child: ${result.childLower} - ${result.childUpper}` : '',
  ].filter(Boolean).join(', ');
};

const isWbcDifferentialResult = (draft: ReportDraft, result: PathologyReportResult, singleReport: boolean) =>
  singleReport && draft.chargeLegacyId === 380 && wbcDifferentialTests.has((result.testParameter ?? '').trim().toUpperCase());

const differentialError = (draft: ReportDraft, singleReport: boolean) => {
  if (draft.reportTemplate || !singleReport || draft.chargeLegacyId !== 380) return '';
  const fields = draft.reportResults.filter((result) => wbcDifferentialTests.has((result.testParameter ?? '').trim().toUpperCase()));
  if (fields.length === 0) return '';
  const total = fields.reduce((sum, result) => sum + (numberOrNull(result.resultValue) ?? 0), 0);
  return Math.abs(total - 100) < 0.01 ? '' : 'All CBC differential values for the listed cell types must still total 100.';
};

const isPathologyReportKind = (kind: InvestigationReportKind | null | undefined) =>
  kind !== 'non-pathology' && kind !== 'radiology';

const UploadReportEditor = ({
  draft,
  bundle,
  singleReport,
  onChange,
}: {
  draft: ReportDraft;
  bundle: PathologyReportBundle;
  singleReport: boolean;
  onChange: (draft: ReportDraft) => void;
}) => {
  const diffError = differentialError(draft, singleReport);
  const isPathology = isPathologyReportKind(draft.reportKind);

  if (draft.reportTemplate) {
    return (
      <div className="pathology-report-template-box">
        <CkEditorField label="Report Template" className="pathology-report-template-editor" value={draft.reportTemplateResult ?? ''} onChange={(value) => onChange({ ...draft, reportTemplateResult: value })} />
      </div>
    );
  }

  return (
    <>
      <h4 className="pathology-report-test-title"><u>{draft.chargeName}</u></h4>
      {isPathology ? <div className="pathology-report-help"><span>** If you want any then just enter - </span><span>(when you print report - not shown in report)</span></div> : null}
      <div className="table-scroll">
        <table className="pathology-report-entry-table">
          <thead>
            <tr>
              <th>Test For</th>
              <th>Test Name</th>
              <th>Result</th>
              <th>Note</th>
              <th>Unit</th>
              <th>Reference Range</th>
            </tr>
          </thead>
          <tbody>
            {draft.reportResults.map((result, index) => {
              const diffField = isWbcDifferentialResult(draft, result, singleReport);
              return (
                <tr key={result.id}>
                  <td>{result.label ?? ''}</td>
                  <td>
                    {result.testParameter ?? ''}
                    {result.method ? <><br /><b>Method : </b>{result.method}</> : null}
                  </td>
                  <td>
                    <input
                      type={diffField ? 'number' : 'text'}
                      step={diffField ? '0.01' : undefined}
                      min={diffField ? '0' : undefined}
                      inputMode={diffField ? 'decimal' : undefined}
                      style={resultStyle(result, bundle)}
                      value={result.resultValue ?? ''}
                      onChange={(event) => {
                        const nextResults = draft.reportResults.map((item, resultIndex) => resultIndex === index ? { ...item, resultValue: event.target.value } : item);
                        onChange({ ...draft, reportResults: nextResults });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={result.comment ?? ''}
                      onChange={(event) => {
                        const nextResults = draft.reportResults.map((item, resultIndex) => resultIndex === index ? { ...item, comment: event.target.value } : item);
                        onChange({ ...draft, reportResults: nextResults });
                      }}
                    />
                  </td>
                  <td>{result.unit ?? ''}</td>
                  <td>{referenceRangeText(result)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="pathology-report-notes-row">
        <div>
          <CkEditorField label="Note" value={draft.note ?? ''} onChange={(value) => onChange({ ...draft, note: value })} />
        </div>
        {isPathology ? (
          <div>
            <CkEditorField label="Extra Note" value={draft.extraNote ?? ''} onChange={(value) => onChange({ ...draft, extraNote: value })} />
          </div>
        ) : null}
      </div>
      {diffError ? <div className="pathology-report-error">{diffError}</div> : null}
    </>
  );
};

export const PathologyReportUploadPage = () => {
  const navigate = useNavigate();
  const { billId } = useParams();
  const [params] = useSearchParams();
  const { showToast } = useToast();
  const lineIds = useMemo(() => (params.get('lineIds') ?? '').split(',').map((id) => id.trim()).filter(Boolean), [params]);
  const query = usePathologyReports(billId, lineIds);
  const mutation = useUpdatePathologyReports(billId ?? '');
  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [approvedDoctorIds, setApprovedDoctorIds] = useState<string[]>([]);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [saveMode, setSaveMode] = useState<'update' | 'print' | null>(null);

  useEffect(() => {
    if (!query.data) return;
    setDrafts(query.data.reports.map(lineToDraft));
    setApprovedDoctorIds(query.data.reports[0]?.approvedDoctors.map((doctor) => doctor.id) ?? []);
  }, [query.data]);

  const singleReport = drafts.length === 1;
  const hasDiffError = drafts.some((draft) => differentialError(draft, singleReport));

  const submit = async (event: { preventDefault: () => void }, printAfterSave: boolean) => {
    event.preventDefault();
    if (!billId || !query.data) return;
    if (hasDiffError) return;
    setSaveMode(printAfterSave ? 'print' : 'update');
    try {
      const attachment = attachmentFile ? await readFileAsAttachment(attachmentFile) : undefined;
      await mutation.mutateAsync({
        reports: drafts.map((draft) => {
          const hasTemplate = Boolean(draft.reportTemplate);
          return {
            lineId: draft.lineId,
            reportTemplateResult: hasTemplate ? draft.reportTemplateResult : null,
            note: hasTemplate ? null : draft.note,
            extraNote: hasTemplate || !isPathologyReportKind(draft.reportKind) ? null : draft.extraNote,
            reportResults: hasTemplate ? [] : draft.reportResults,
          };
        }),
        approvedDoctorIds,
        ...(attachment ? { attachment } : {}),
      });
      showToast('Result update successfully.', 'success');
      if (printAfterSave) openAuthenticatedRoute(`/pathology/report-print/${billId}?lineIds=${drafts.map((draft) => draft.lineId).join(',')}`);
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'The report could not be saved.', 'error');
    } finally {
      setSaveMode(null);
    }
  };

  if (lineIds.length === 0) return <ErrorState title="No reports selected" message="Select one or more generated report rows first." />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Report not found" message="The selected report could not be loaded." />;
  if (query.data.reports.length === 0) return <EmptyState title="No report lines found" message="The selected bill has no matching report rows." />;

  const bundle = query.data;
  const title = drafts.length > 1 ? 'BULK REPORT UPLOAD' : 'REPORT UPLOAD';
  const firstReport = bundle.reports[0];
  const isPathology = isPathologyReportKind(firstReport?.reportKind);

  return (
    <div className="page-stack pathology-page">
      <div className="content-card pathology-report-upload-card">
        <div className="pathology-report-upload-header"><div className="pathology-report-upload-header-row"><h4>{title}</h4><button className="button button-secondary" type="button" onClick={() => navigate(-1)}>Back</button></div></div>
        <form onSubmit={(event) => void submit(event, false)}>
          <div className="pathology-report-upload-body">
            <div className="pathology-report-info-grid">
              <table>
                <tbody>
                  <tr><td><span>Patient Name </span></td><td>{bundle.patientSnapshot.name} ({bundle.patientSnapshot.uhid ?? bundle.patientId ?? '-'})</td></tr>
                  <tr><td><span>Patient Age </span></td><td>{ageDisplay(bundle.patientSnapshot)}</td></tr>
                  <tr><td><span>Mobile No.</span></td><td>{bundle.patientSnapshot.phone ?? '-'}</td></tr>
                  <tr><td><span>Gender</span></td><td>{bundle.patientSnapshot.gender}</td></tr>
                </tbody>
              </table>
              <table>
                <tbody>
                  <tr><td><span>Bill Date </span></td><td>{formatPathologyDateTime(bundle.billDate)}</td></tr>
                  {isPathology ? (
                    <>
                      {firstReport?.sampleCollectedAt ? <tr><td><span>Collection By (Date) :</span></td><td>{formatPathologyDateTime(firstReport.sampleCollectedAt)}</td></tr> : null}
                      {firstReport?.labReceivedAt ? <tr><td><span>Lab Receive By (Date) :</span></td><td>{formatPathologyDateTime(firstReport.labReceivedAt)}</td></tr> : null}
                    </>
                  ) : (
                    <>
                      <tr><td><span>Reffer By :</span></td><td>{bundle.referredBy?.name ?? '-'}</td></tr>
                      <tr><td><span>Under Doctor :</span></td><td>{bundle.referringDoctor?.name ?? '-'}</td></tr>
                    </>
                  )}
                  <tr><td><span>Report Date :</span></td><td>{formatPathologyDateTime(firstReport?.reportGeneratedAt ?? firstReport?.reportUpdatedAt ?? new Date().toISOString())}</td></tr>
                </tbody>
              </table>
            </div>

            {drafts.map((draft, index) => (
              <section className="pathology-report-upload-section" key={draft.lineId}>
                <UploadReportEditor draft={draft} bundle={bundle} singleReport={singleReport} onChange={(next) => setDrafts((current) => updateDraft(current, draft.lineId, () => next))} />
                {index < drafts.length - 1 ? <hr /> : null}
              </section>
            ))}

            <div className="pathology-report-meta-row">
              {isPathology && drafts.length === 1 ? (
                <>
                  <div><span>Vial :</span><strong>{drafts[0]?.vial ?? ''}</strong></div>
                  <div><span>Sample :</span><strong>{drafts[0]?.sample ?? ''}</strong></div>
                  <div><span>Instrument Used :</span><strong>{drafts[0]?.instrumentUsed ?? ''}</strong></div>
                </>
              ) : <div className="pathology-report-meta-spacer" />}
              <div><span>Report update By :</span><strong>{bundle.printedBy.name || '-'}</strong></div>
              <label>
                Select Approval Doctor
                <select multiple value={approvedDoctorIds} onChange={(event) => setApprovedDoctorIds(Array.from(event.currentTarget.selectedOptions).map((option) => option.value))}>
                  {bundle.approvalDoctorOptions.map((doctor) => <option key={doctor.id} value={doctor.id}>Dr. {doctor.name}</option>)}
                </select>
              </label>
              <label>
                Attach Document
                <input type="file" onChange={(event) => setAttachmentFile(event.currentTarget.files?.[0] ?? null)} />
              </label>
            </div>

            {bundle.reports.some((report) => report.attachment) ? (
              <div className="pathology-report-attachments">
                {bundle.reports.map((report) => report.attachment ? <a key={report.lineId} href={report.attachment.dataUrl} download={report.attachment.name}>{report.chargeName}: {report.attachment.name}</a> : null)}
              </div>
            ) : null}

            <div className="pathology-report-actions">
              <button className="button button-primary" type="submit" disabled={mutation.isPending || hasDiffError}>{saveMode === 'update' ? 'Updating...' : 'Update Report'}</button>
              <button className="button button-danger" type="button" disabled={mutation.isPending || hasDiffError} onClick={(event) => void submit(event, true)}><IconPrinter /> {saveMode === 'print' ? 'Updating...' : 'Update Report & Print'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
